import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';

import { AdminSettingsTypeEnum, FundTypeEnum, GoldTypeEnum, MetalTypeEnum, ModuleEnum, PermissionsEnum, ProductStatusEnum, SilverTypeEnum } from '~utils/enums';
import UtilsFunctions from '~utils/functions';

import * as Styles from './_productSettingDetailsForm.module.scss';
import Checkbox from '~components/atoms/Checkbox';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { TDropdownOption } from '~types/components/labelledDropdown';

const ProductSettingDetailsForm = () => {
    const {
        Misc:
        {
            blockEnterKey,
            dateTimeFormat,
        },
    } = UtilsFunctions;

    const dispatch = useAppDispatch();

    const productSettingDetailsModalIsEditing = useAppSelector(state => state.setting.modals.productSettingDetailsModalIsEditing);
    const productSettingDetailsModalIsDelete = useAppSelector(state => state.setting.modals.productSettingDeleteProductModalIsOpen);
    const productSettingDetails = useAppSelector(state => state.setting.productSettingDetails);
    const goldProducts = useAppSelector(state => state.gold.allProducts);
    const silverProducts = useAppSelector(state => state.silver.allProducts);
    const access = useAppSelector(state => state.auth.userDetails.access);

    const isSilver = productSettingDetails.metalType === MetalTypeEnum.Silver;
    const isGold = productSettingDetails.metalType === MetalTypeEnum.Gold;
    const allProducts = isSilver ? silverProducts : goldProducts;

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Product);

    const toggleIsEditing = () => dispatch(combinedActions.settingSetProductSettingDetailsModalIsEditing(!productSettingDetailsModalIsEditing));

    const fundTypeOptions = Object.entries(FundTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label, value }));

    const goldTypeOptions = Object.entries(GoldTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label: label.replace(/([a-z])([A-Z])/g, '$1 $2'), value }));

    const ProductSettingDetailsFormValidation = () => Yup.object().shape({
        fundType: Yup.string()
            .test('check-branch-name',
                'Selected fund type does not exist',
                function (value) {
                    if (!value) return false;

                    return !!Object.keys(FundTypeEnum).find((fundType) => fundType === value);
                }
            ).required('Fund Type is required'),
        physicalGoldWeight: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => !isSilver && !formik.values.goldTypeToggle,
                then: (schema) => schema.required('Gold Weight is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        physicalSilverWeight: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => isSilver,
                then: (schema) => schema.required('Silver Weight is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        goldPriceBankBuy: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => !isSilver && !formik.values.goldPriceBankBuyApi,
                then: (schema) => schema.required('Amount is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        silverPriceBankBuy: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => isSilver && !formik.values.silverPriceBankBuyApi,
                then: (schema) => schema.required('Amount is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        goldPriceBankSell: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => !isSilver && !formik.values.goldPriceBankSellApi,
                then: (schema) => schema.required('Amount is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        silverPriceBankSell: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => isSilver && !formik.values.silverPriceBankSellApi,
                then: (schema) => schema.required('Amount is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        affinBankBuySplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('Affin Bank Buy Split is required'),
        tttBankBuySplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('TTT Bank Buy Split is required'),
        affinBankSellSplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('Affin Bank Sell Split is required'),
        tttBankSellSplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('TTT Bank Sell Split is required'),
        mintingCost: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('Minting Cost is required'),
        lowGoldAlert: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => !isSilver,
                then: (schema) => schema.required('Low Gold Alert is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        lowSilverAlert: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => isSilver,
                then: (schema) => schema.required('Low Silver Alert is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),

        // toggle checking handler
        goldPriceBankBuyApi: Yup.boolean().required('Must have Bank Buy type'),
        silverPriceBankBuyApi: Yup.boolean().required('Must have Bank Buy type'),
        goldPriceBankSellApi: Yup.boolean().required('Must have Bank Sell type'),
        silverPriceBankSellApi: Yup.boolean().required('Must have Bank Sell type'),
        goldTypeToggle: Yup.boolean().required('Must have Gold Type'),
        affinBankBuySplitToggle: Yup.boolean().required('Must have Affin Bank Buy type'),
        tttBankBuySplitToggle: Yup.boolean().required('Must have TTT Bank Buy type'),
        affinBankSellSplitToggle: Yup.boolean().required('Must have Affin Bank Sell type'),
        tttBankSellSplitToggle: Yup.boolean().required('Must have TTT Bank Sell type'),
        mintingCostToggle: Yup.boolean().required('Must have Minting Cost type'),
    });

    const getSplitValues = (fixedValue: string | undefined, percentageValue: string | undefined) => {
        if (fixedValue){
            const shifted = ((parseFloat(fixedValue) * 100) / 100);

            if (Number.isInteger(shifted * 100)) {
                return shifted.toFixed(2);
            }

            return parseFloat(shifted.toFixed(6)).toString();
        }

        if (percentageValue){
            const shifted = parseFloat(percentageValue) * 100;

            if (Number.isInteger(shifted * 100)) {
                return shifted.toFixed(2);
            }

            return parseFloat(shifted.toFixed(6)).toString();
        }

        return '0';
    };

    const ProductSettingDetailsFormInitialValues = {
        fundType: productSettingDetails.fundType ? FundTypeEnum[productSettingDetails.fundType] : '',
        physicalGoldWeight: !isSilver && productSettingDetails.physicalGoldWeight ? productSettingDetails.physicalGoldWeight : '',
        physicalSilverWeight: isSilver && productSettingDetails.physicalSilverWeight ? productSettingDetails.physicalSilverWeight : '',
        goldPriceBankBuy: !isSilver && productSettingDetails.fixedBankBuyGoldPrice ? ((parseFloat(productSettingDetails.fixedBankBuyGoldPrice) * 100) / 100).toFixed(2).toString() : '',
        silverPriceBankBuy: isSilver && productSettingDetails.fixedBankBuySilverPrice ? ((parseFloat(productSettingDetails.fixedBankBuySilverPrice) * 100) / 100).toFixed(2).toString() : '',
        goldPriceBankSell: !isSilver && productSettingDetails.fixedBankSellGoldPrice ? ((parseFloat(productSettingDetails.fixedBankSellGoldPrice) * 100) / 100).toFixed(2).toString() : '',
        silverPriceBankSell: isSilver && productSettingDetails.fixedBankSellSilverPrice ? ((parseFloat(productSettingDetails.fixedBankSellSilverPrice) * 100) / 100).toFixed(2).toString() : '',
        affinBankBuySplit: getSplitValues(productSettingDetails.affinBankBuySplit, productSettingDetails.affinBankBuySplitPercentage),
        tttBankBuySplit: getSplitValues(productSettingDetails.tttBankBuySplit, productSettingDetails.tttBankBuySplitPercentage),
        affinBankSellSplit: getSplitValues(productSettingDetails.affinBankSellSplit, productSettingDetails.affinBankSellSplitPercentage),
        tttBankSellSplit: getSplitValues(productSettingDetails.tttBankSellSplit, productSettingDetails.tttBankSellSplitPercentage),
        mintingCost: getSplitValues(productSettingDetails.mintingCost, productSettingDetails.mintingCostPercentage),
        lowGoldAlert: !isSilver && productSettingDetails?.lowGoldAlert
            ? (parseFloat(productSettingDetails?.lowGoldAlert) * 100).toFixed(2) : '',
        lowSilverAlert: isSilver && productSettingDetails?.lowSilverAlert
            ? (parseFloat(productSettingDetails?.lowSilverAlert) * 100).toFixed(2) : '',

        // toggle checking handler
        goldPriceBankBuyApi: !isSilver && !productSettingDetails.fixedBankBuyGoldPrice,
        silverPriceBankBuyApi: isSilver && !productSettingDetails.fixedBankBuySilverPrice,
        goldPriceBankSellApi: !isSilver && !productSettingDetails.fixedBankSellGoldPrice,
        silverPriceBankSellApi: isSilver && !productSettingDetails.fixedBankSellSilverPrice,
        goldTypeToggle: !isSilver && productSettingDetails.goldType === GoldTypeEnum.PaperGold,
        affinBankBuySplitToggle: !productSettingDetails.affinBankBuySplit,
        tttBankBuySplitToggle: !productSettingDetails.tttBankBuySplit,
        affinBankSellSplitToggle: !productSettingDetails.affinBankSellSplit,
        tttBankSellSplitToggle: !productSettingDetails.tttBankSellSplit,
        mintingCostToggle: !productSettingDetails.mintingCost,
    };

    const formik = useFormik({
        initialValues: ProductSettingDetailsFormInitialValues,
        validationSchema: ProductSettingDetailsFormValidation(),
        onSubmit: (values) => {
            const {
                goldPriceBankBuy,
                silverPriceBankBuy,
                goldPriceBankSell,
                silverPriceBankSell,
                affinBankBuySplit,
                tttBankBuySplit,
                affinBankSellSplit,
                tttBankSellSplit,
                mintingCost,
                lowGoldAlert,
                lowSilverAlert,
                affinBankBuySplitToggle,
                tttBankBuySplitToggle,
                affinBankSellSplitToggle,
                tttBankSellSplitToggle,
                mintingCostToggle,
                goldPriceBankBuyApi,
                silverPriceBankBuyApi,
                goldPriceBankSellApi,
                silverPriceBankSellApi,
                goldTypeToggle,
                fundType,
                physicalGoldWeight,
                physicalSilverWeight,
            } = values;

            const { id, isEditable, createdAt, updatedAt, status, fixedBankBuyGoldPrice: _fbbgp,
                fixedBankSellGoldPrice: _fbsgp,
                fixedBankBuySilverPrice: _fbbsp,
                fixedBankSellSilverPrice: _fbssp,
                physicalGoldWeight: _pgw,
                physicalSilverWeight: _psw,
                lowGoldAlert: _lga,
                lowSilverAlert: _lsa,
                goldType: _gt,
                silverType: _st, ...restOfProductDetails } = productSettingDetails;

            const basePayload = {
                ...restOfProductDetails,
                type: AdminSettingsTypeEnum.Product,
                productId: productSettingDetails.productId,
                productName: productSettingDetails.productName,
                fundType: FundTypeEnum[fundType as keyof typeof FundTypeEnum],
                affinBankBuySplitPercentage: affinBankBuySplitToggle ? affinBankBuySplit : undefined,
                tttBankBuySplitPercentage: tttBankBuySplitToggle ? tttBankBuySplit : undefined,
                affinBankSellSplitPercentage: affinBankSellSplitToggle ? affinBankSellSplit : undefined,
                tttBankSellSplitPercentage: tttBankSellSplitToggle ? tttBankSellSplit : undefined,
                mintingCostPercentage: mintingCostToggle ? mintingCost : undefined,
                affinBankBuySplit: !affinBankBuySplitToggle ? affinBankBuySplit : undefined,
                tttBankBuySplit: !tttBankBuySplitToggle ? tttBankBuySplit : undefined,
                affinBankSellSplit: !affinBankSellSplitToggle ? affinBankSellSplit : undefined,
                tttBankSellSplit: !tttBankSellSplitToggle ? tttBankSellSplit : undefined,
                mintingCost: !mintingCostToggle ? mintingCost : undefined,
                productStatus: productSettingDetailsModalIsDelete ? ProductStatusEnum.Deactivated : ProductStatusEnum.Active,
            };

            const metalSpecificPayload = isSilver
                ? {
                    silverType: SilverTypeEnum.PhysicalSilver,
                    physicalSilverWeight: physicalSilverWeight,
                    lowSilverAlert: (parseFloat(lowSilverAlert) / 100).toString(),
                    fixedBankBuySilverPrice: silverPriceBankBuyApi ? undefined : silverPriceBankBuy,
                    fixedBankSellSilverPrice: silverPriceBankSellApi ? undefined : silverPriceBankSell,
                    fixedBankBuyGoldPrice: undefined,
                    fixedBankSellGoldPrice: undefined,
                    physicalGoldWeight: undefined,
                    lowGoldAlert: undefined,
                    goldType: undefined,
                }
                : {
                    goldType: GoldTypeEnum.PhysicalGold,
                    physicalGoldWeight: !goldTypeToggle ? physicalGoldWeight : null,
                    lowGoldAlert: (parseFloat(lowGoldAlert) / 100).toString(),
                    fixedBankBuyGoldPrice: goldPriceBankBuyApi ? undefined : goldPriceBankBuy,
                    fixedBankSellGoldPrice: goldPriceBankSellApi ? undefined : goldPriceBankSell,
                    fixedBankBuySilverPrice: undefined,
                    fixedBankSellSilverPrice: undefined,
                    physicalSilverWeight: undefined,
                    lowSilverAlert: undefined,
                    silverType: undefined,
                };

            console.log('isSilver:', isSilver);
            console.log('metalSpecificPayload:', metalSpecificPayload);
            console.log('SilverTypeEnum.PhysicalSilver:', SilverTypeEnum.PhysicalSilver);
            console.log('full payload:', { ...basePayload, ...metalSpecificPayload });

            dispatch(combinedActions.settingUpdateSettingsAttempt({
                ...basePayload,
                ...metalSpecificPayload,
            }));
        },
    });

    useEffect(() => {
        if (!productSettingDetailsModalIsEditing) {
            formik.resetForm();
        }
    }, [productSettingDetailsModalIsEditing]);

    const handleEditClick = () => {
        toggleIsEditing();
        formik.resetForm();
    };

    const handleChangeGoldType = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'goldType', value: e.target.value } });

        if (e.target.value === 'Paper Gold'){
            formik.handleChange({ target: { name: 'goldType', value: 'Paper Gold' } });
            formik.handleChange({ target: { name: 'physicalGoldWeight', value: '' } });
            formik.setFieldValue('goldTypeToggle', true);
        }
        if (e.target.value === 'Physical Gold'){
            formik.handleChange({ target: { name: 'goldType', value: 'Physical Gold' } });
            formik.setFieldValue('goldTypeToggle', false);
        }
    };

    const handleSelectGoldType = (e: TDropdownOption) => {
        if (e.value === GoldTypeEnum.PaperGold){
            formik.handleChange({ target: { name: 'goldType', value: 'Paper Gold' } });
            formik.handleChange({ target: { name: 'physicalGoldWeight', value: '' } });
            formik.setFieldValue('goldTypeToggle', true);
        }
        if (e.value === GoldTypeEnum.PhysicalGold){
            formik.handleChange({ target: { name: 'goldType', value: 'Physical Gold' } });
            formik.setFieldValue('goldTypeToggle', false);
        }

        formik.setFieldTouched('goldType', false);
    };

    const renderProductSettingDetails = () => {
        const productName = productSettingDetails.productName;

        return (
            <form
                onSubmit={formik.handleSubmit}
                className={Styles['form-container']}
                onKeyDown={blockEnterKey}
            >
                <div className={Styles['form-subcontainer']}>
                    <div>Product ID</div>

                    <LabelledInput
                        label=':'
                        value={productSettingDetails.productId?.toString()}
                        disabled
                        isRow
                    />

                    <div>Product Name</div>

                    <LabelledInput
                        label=':'
                        value={productName}
                        disabled
                        isRow
                    />

                    <div>Created At</div>

                    <LabelledInput
                        label=':'
                        value={dateTimeFormat(productSettingDetails.createdAt)}
                        disabled
                        isRow
                    />

                    <div>Updated At</div>

                    <LabelledInput
                        label=':'
                        value={dateTimeFormat(productSettingDetails.updatedAt)}
                        disabled
                        isRow
                    />

                    <div>Fund Type</div>

                    <div className={Styles['flex-span-container']}>
                        <LabelledDropdown
                            id='fundType'
                            name='fundType'
                            label=':'
                            options={fundTypeOptions}
                            value={formik.values.fundType || ''}
                            error={formik.touched.fundType ? formik.errors.fundType : undefined}
                            onChange={(e) => formik.handleChange({ target: { name: 'fundType', value: e.target.value } })}
                            onSelectOption={(e) => formik.handleChange({ target: { name: 'fundType', value: e.value } })}
                            onBlur={formik.handleBlur}
                            noOptionsMessage='No branches found'
                            autoComplete='off'
                            optionSelected={!!fundTypeOptions.find((option) => option.label === formik.values.fundType)}
                            disabled={!productSettingDetailsModalIsEditing}
                            isRow
                        />

                        <div></div>
                    </div>

                    {/* <div>Gold Type</div>

                    <div className={Styles['flex-span-container']}>
                        <LabelledDropdown
                            id='goldType'
                            name='goldType'
                            label=':'
                            options={goldTypeOptions}
                            value={formik.values.goldType || ''}
                            error={formik.touched.goldType ? formik.errors.goldType : undefined}
                            onChange={handleChangeGoldType}
                            onSelectOption={handleSelectGoldType}
                            onBlur={formik.handleBlur}
                            noOptionsMessage='No gold type found'
                            autoComplete='off'
                            optionSelected={!!goldTypeOptions.find((option) => option.label === formik.values.goldType)}
                            disabled={!productSettingDetailsModalIsEditing}
                            isRow
                        />

                        <div></div>
                    </div> */}

                    <div>Physical Weight (gram)</div>

                    <div className={Styles['flex-span-container']}>
                        {!isSilver ? (
                            <LabelledInput
                                id='physicalGoldWeight'
                                name='physicalGoldWeight'
                                label=':'
                                value={formik.values.physicalGoldWeight}
                                error={formik.touched.physicalGoldWeight ? formik.errors.physicalGoldWeight : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing || formik.values.goldTypeToggle}
                                isRow
                            />
                        ) : (
                            <LabelledInput
                                id='physicalSilverWeight'
                                name='physicalSilverWeight'
                                label=':'
                                value={formik.values.physicalSilverWeight}
                                error={formik.touched.physicalSilverWeight ? formik.errors.physicalSilverWeight : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing}
                                isRow
                            />
                        )}

                        <div></div>
                    </div>

                    <div>{isSilver ? 'Silver' : 'Gold'} Price Bank Buy (RM)*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            {!isSilver ? (
                                <LabelledInput
                                    id='goldPriceBankBuy'
                                    name='goldPriceBankBuy'
                                    label=':'
                                    value={formik.values.goldPriceBankBuy}
                                    error={formik.touched.goldPriceBankBuy ? formik.errors.goldPriceBankBuy : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={!productSettingDetailsModalIsEditing || formik.values.goldPriceBankBuyApi}
                                    isRow
                                />
                            ) : (
                                <LabelledInput
                                    id='silverPriceBankBuy'
                                    name='silverPriceBankBuy'
                                    label=':'
                                    value={formik.values.silverPriceBankBuy}
                                    error={formik.touched.silverPriceBankBuy ? formik.errors.silverPriceBankBuy : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={!productSettingDetailsModalIsEditing || formik.values.silverPriceBankBuyApi}
                                    isRow
                                />
                            )}
                        </div>

                        <div className={Styles['flex-div']}>
                            <Checkbox
                                label=''
                                isChecked={isSilver ? formik.values.silverPriceBankBuyApi : formik.values.goldPriceBankBuyApi}
                                onChange={() => {
                                    if (productSettingDetailsModalIsEditing) {
                                        const apiFieldName = isSilver ? 'silverPriceBankBuyApi' : 'goldPriceBankBuyApi';
                                        const priceFieldName = isSilver ? 'silverPriceBankBuy' : 'goldPriceBankBuy';
                                        if (!formik.values[apiFieldName as keyof typeof formik.values]) {
                                            formik.setFieldValue(priceFieldName, '');
                                            formik.setFieldTouched(priceFieldName, false);
                                        }

                                        formik.setFieldValue(apiFieldName, !formik.values[apiFieldName as keyof typeof formik.values]);
                                    }
                                }}
                            />

                            <p>Use API</p>
                        </div>
                    </div>

                    <div>{isSilver ? 'Silver' : 'Gold'} Price Bank Sell (RM)*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            {!isSilver ? (
                                <LabelledInput
                                    id='goldPriceBankSell'
                                    name='goldPriceBankSell'
                                    label=':'
                                    value={formik.values.goldPriceBankSell}
                                    error={formik.touched.goldPriceBankSell ? formik.errors.goldPriceBankSell : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={!productSettingDetailsModalIsEditing || formik.values.goldPriceBankSellApi}
                                    isRow
                                />
                            ) : (
                                <LabelledInput
                                    id='silverPriceBankSell'
                                    name='silverPriceBankSell'
                                    label=':'
                                    value={formik.values.silverPriceBankSell}
                                    error={formik.touched.silverPriceBankSell ? formik.errors.silverPriceBankSell : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={!productSettingDetailsModalIsEditing || formik.values.silverPriceBankSellApi}
                                    isRow
                                />
                            )}
                        </div>

                        <div className={Styles['flex-div']}>
                            <Checkbox
                                label=''
                                isChecked={isSilver ? formik.values.silverPriceBankSellApi : formik.values.goldPriceBankSellApi}
                                onChange={() => {
                                    if (productSettingDetailsModalIsEditing) {
                                        const apiFieldName = isSilver ? 'silverPriceBankSellApi' : 'goldPriceBankSellApi';
                                        const priceFieldName = isSilver ? 'silverPriceBankSell' : 'goldPriceBankSell';
                                        if (!formik.values[apiFieldName as keyof typeof formik.values]) {
                                            formik.setFieldValue(priceFieldName, '');
                                            formik.setFieldTouched(priceFieldName, false);
                                        }

                                        formik.setFieldValue(apiFieldName, !formik.values[apiFieldName as keyof typeof formik.values]);
                                    }}}
                            />

                            <p>Use API</p>
                        </div>
                    </div>

                    <div>Affin Bank Buy Split ({formik.values.affinBankBuySplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='affinBankBuySplit'
                                name='affinBankBuySplit'
                                label=':'
                                value={formik.values.affinBankBuySplit}
                                error={formik.touched.affinBankBuySplit ? formik.errors.affinBankBuySplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.affinBankBuySplitToggle}
                                    onChange={() => {if (productSettingDetailsModalIsEditing) formik.setFieldValue('affinBankBuySplitToggle', !formik.values.affinBankBuySplitToggle);}}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.affinBankBuySplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>TTT Bank Buy Split ({formik.values.tttBankBuySplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='tttBankBuySplit'
                                name='tttBankBuySplit'
                                label=':'
                                value={formik.values.tttBankBuySplit}
                                error={formik.touched.tttBankBuySplit ? formik.errors.tttBankBuySplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.tttBankBuySplitToggle}
                                    onChange={() => {if (productSettingDetailsModalIsEditing) formik.setFieldValue('tttBankBuySplitToggle', !formik.values.tttBankBuySplitToggle);}}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.tttBankBuySplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>Affin Bank Sell Split ({formik.values.affinBankSellSplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='affinBankSellSplit'
                                name='affinBankSellSplit'
                                label=':'
                                value={formik.values.affinBankSellSplit}
                                error={formik.touched.affinBankSellSplit ? formik.errors.affinBankSellSplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.affinBankSellSplitToggle}
                                    onChange={() => {if (productSettingDetailsModalIsEditing) formik.setFieldValue('affinBankSellSplitToggle', !formik.values.affinBankSellSplitToggle);}}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.affinBankSellSplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>TTT Bank Sell Split ({formik.values.tttBankSellSplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='tttBankSellSplit'
                                name='tttBankSellSplit'
                                label=':'
                                value={formik.values.tttBankSellSplit}
                                error={formik.touched.tttBankSellSplit ? formik.errors.tttBankSellSplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.tttBankSellSplitToggle}
                                    onChange={() => {if (productSettingDetailsModalIsEditing) formik.setFieldValue('tttBankSellSplitToggle', !formik.values.tttBankSellSplitToggle);}}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.tttBankSellSplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>Minting Cost ({formik.values.mintingCostToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='mintingCost'
                                name='mintingCost'
                                label=':'
                                value={formik.values.mintingCost}
                                error={formik.touched.mintingCost ? formik.errors.mintingCost : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!productSettingDetailsModalIsEditing}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.mintingCostToggle}
                                    onChange={() => {if (productSettingDetailsModalIsEditing) formik.setFieldValue('mintingCostToggle', !formik.values.mintingCostToggle);}}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.mintingCostToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>{isSilver ? 'Silver' : 'Gold'} Alert Threshold (%)*</div>

                    <LabelledInput
                        id={isSilver ? 'lowSilverAlert' : 'lowGoldAlert'}
                        name={isSilver ? 'lowSilverAlert' : 'lowGoldAlert'}
                        label=':'
                        value={isSilver ? formik.values.lowSilverAlert : formik.values.lowGoldAlert}
                        error={isSilver ? (formik.touched.lowSilverAlert ? formik.errors.lowSilverAlert : undefined) : (formik.touched.lowGoldAlert ? formik.errors.lowGoldAlert : undefined)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!productSettingDetailsModalIsEditing}
                        isRow
                    />
                </div>

                <ActionButton
                    label={productSettingDetailsModalIsDelete ? 'Delete' : 'Update'}
                    type='submit'
                    className={`${productSettingDetailsModalIsDelete && Styles.danger} ${Styles['submit-button']}`}
                    disabled={productSettingDetailsModalIsDelete ? false : !productSettingDetailsModalIsEditing || !formik.dirty}
                />
            </form>
        );
    };

    return (
        <div className={Styles.container}>
            {hasEditAccess
                && productSettingDetails.isEditable
                && !productSettingDetailsModalIsDelete
                &&
                (
                    <ActionButton
                        label={productSettingDetailsModalIsEditing ? 'Disable editing' : 'Enable editing'}
                        onClick={handleEditClick}
                        className={Styles['edit-button']}
                    />
                )
            }

            {renderProductSettingDetails()}
        </div>
    );
};

export default ProductSettingDetailsForm;
